import { merge } from 'lodash';
import { useCallback } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

import { type ShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type UpdateShortIdParams, useUpdateShortId } from '~/swr-hooks/shortUrl/useUpdateShortId';

type UseUpdateShareCanvasLinkParams = {
  canvasId: string;
  shortIdInfo: ShortUrlResponse;
};

export const useUpdateShareCanvasLink = ({ canvasId, shortIdInfo }: UseUpdateShareCanvasLinkParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { updateShortId } = useUpdateShortId();

  const onShortUrlUpdate = useCallback(
    async (
      props: DeepPartial<Omit<UpdateShortIdParams, 'objectId' | 'id' | 'boardContext' | 'shortId'>>,
    ): Promise<ShareUrlResponseWithSource> => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      const response = await updateShortId.mutateAsync({
        workspaceId,
        ...merge({}, shortIdInfo, props),
        objectId: canvasId,
        shortId: shortIdInfo.id,
        boardContext: undefined,
      });

      return {
        ...response,
        objectId: canvasId,
        objectType: 'canvas',
      };
    },
    [canvasId, currentWorkspace?.id, shortIdInfo, updateShortId],
  );

  return {
    onShortUrlUpdate,
    isLoading: updateShortId.isPending,
  };
};
